type TranslateFn = (key: string, options?: Record<string, string | number>) => string;

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

/**
 * Render an elapsed/remaining span as a short localized duration ("45s", "5m 03s", "1h 12m").
 * Sagas routinely run for several minutes, and printing the raw second count ("300s elapsed")
 * makes the reader do the arithmetic; anything under a minute stays in seconds, where the
 * second-by-second change is the signal that something is happening.
 *
 * Seconds are zero-padded only when a larger unit precedes them, so the digits don't jump
 * width between polls (paired with `font-variant-numeric: tabular-nums` at the call sites).
 */
export function formatShortDuration(ms: number, t: TranslateFn): string {
  const totalSeconds = Math.max(0, Math.round((Number.isFinite(ms) ? ms : 0) / MS_PER_SECOND));

  if (totalSeconds < SECONDS_PER_MINUTE) {
    return t('time.durationSeconds', { seconds: totalSeconds });
  }

  if (totalSeconds < SECONDS_PER_HOUR) {
    const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
    const seconds = totalSeconds % SECONDS_PER_MINUTE;
    if (seconds === 0) {
      return t('time.durationMinutes', { minutes });
    }
    return t('time.durationMinutesSeconds', { minutes, seconds: String(seconds).padStart(2, '0') });
  }

  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  return t('time.durationHoursMinutes', { hours, minutes: String(minutes).padStart(2, '0') });
}

export default formatShortDuration;
